/** The Header/Detail Document screen — the frontend half of the archetype.
 *
 * Backend counterpart: archetypes/document.py. Together they cover ~135 of
 * GHERP's 449 screens: every Sales Order, Purchase Order, GRN, Invoice,
 * Issue, Work Order and BOM.
 *
 * UI_TASTE.md Section A is unusually specific about this screen, because it
 * is the one people spend their day in:
 *
 *   "header fields at the top and an editable line-item grid below —
 *    add-row / delete-row, per-line quantity x rate = amount, and a document
 *    total that recomputes live. The grid is keyboard-friendly (tab across,
 *    Enter for a new row) because this is high-volume data entry, not
 *    occasional form filling."
 *
 * And the rule that matters most:
 *
 *   "the same computation is re-done server-side on save — the browser is
 *    never the source of the financial figure."
 *
 * So the totals below are for the person typing. They are recomputed by
 * compute_totals() in Python before anything is written, and whatever this
 * screen sent is discarded.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { saveFile, type ExportedFile } from '@/lib/download'
import type { DocStatus, DocumentSpec, LineColumn, ListResult } from '@/types'
import { singularOf } from '@/lib/label'
import { useObjectRights } from '@/lib/rights'

type Row = Record<string, unknown>
type Line = Record<string, string>

const BADGE: Record<DocStatus, string> = {
  Draft: 'badge-draft', Submitted: 'badge-submitted',
  Authorised: 'badge-authorised', Closed: 'badge-closed',
  Cancelled: 'badge-cancelled',
}

/** Mirrors TRANSITIONS in archetypes/document.py. The server is the control;
 *  this only decides which buttons are worth showing. */
const NEXT: Record<DocStatus, DocStatus[]> = {
  Draft: ['Submitted', 'Cancelled'],
  Submitted: ['Authorised', 'Draft', 'Cancelled'],
  Authorised: ['Closed', 'Cancelled'],
  Closed: [], Cancelled: [],
}

const ACTION_LABEL: Partial<Record<DocStatus, string>> = {
  Submitted: 'Submit', Authorised: 'Authorise',
  Draft: 'Return to Draft', Closed: 'Close', Cancelled: 'Cancel',
}

function money(v: unknown): string {
  const n = Number(v)
  return Number.isFinite(n)
    ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—'
}

function blankLine(spec: DocumentSpec): Line {
  const l: Line = {}
  for (const c of spec.lineColumns) l[c.key] = ''
  return l
}

export function DocumentScreen({ spec }: { spec: DocumentSpec }) {
  // What this person may do here — for deciding what to SHOW. The server
  // checks every request: add to create, modify to edit or move a document,
  // approve to authorise it (archetypes/document.py). RIGHTS_MODEL.md 6.1.
  const rights = useObjectRights(spec.module, spec.object)
  const [list, setList] = useState<ListResult<Row> | null>(null)
  const [open, setOpen] = useState<Row | null | undefined>(undefined)
  const [lines, setLines] = useState<Line[]>([])
  const [header, setHeader] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const gridRef = useRef<HTMLTableSectionElement>(null)

  const valuesVisible = list?.values_visible ?? false
  const qtyKey = spec.qtyKey ?? 'qty'
  const rateKey = spec.rateKey ?? 'rate'

  const load = useCallback(async () => {
    try {
      setList(await api.get<ListResult<Row>>(`/${spec.path}`))
    } catch (e) {
      setErrors([e instanceof ApiError ? e.message : 'Please refresh and try again.'])
    } finally { setLoading(false) }
  }, [spec.path])

  useEffect(() => { void load() }, [load])

  // --- live totals, for the person typing -------------------------------
  const lineAmount = useCallback((l: Line) => {
    const q = Number(l[qtyKey] || 0)
    const r = Number(l[rateKey] || 0)
    return Number.isFinite(q) && Number.isFinite(r) ? q * r : 0
  }, [qtyKey, rateKey])

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + lineAmount(l), 0),
    [lines, lineAmount])

  // --- grid editing ------------------------------------------------------
  const setCell = (i: number, key: string, v: string) =>
    setLines(prev => prev.map((l, n) => n === i ? { ...l, [key]: v } : l))

  const addLine = () => setLines(prev => [...prev, blankLine(spec)])

  const removeLine = (i: number) =>
    setLines(prev => prev.length === 1 ? prev : prev.filter((_, n) => n !== i))

  /** Enter on the last row adds another. This is the difference between a
   *  form somebody fills in occasionally and a grid somebody works in all
   *  day — UI_TASTE.md is explicit that this screen is the latter. */
  function onGridKeyDown(e: React.KeyboardEvent, rowIndex: number) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (rowIndex === lines.length - 1) addLine()
    // Focus moves to the first editable cell of the next row after render.
    requestAnimationFrame(() => {
      const rows = gridRef.current?.querySelectorAll('tr')
      const next = rows?.[rowIndex + 1]?.querySelector('input, select')
      ;(next as HTMLElement | null)?.focus()
    })
  }

  function startNew() {
    setHeader({}); setLines([blankLine(spec)]); setErrors([]); setOpen(null)
  }

  async function openDocument(row: Row) {
    setBusy(true); setErrors([])
    try {
      const doc = await api.get<{ header: Row; lines: Row[] }>(
        `/${spec.path}/${String(row[spec.idKey])}`)
      const h: Record<string, string> = {}
      for (const f of spec.headerFields) {
        h[f.key] = doc.header[f.key] == null ? '' : String(doc.header[f.key])
      }
      setHeader(h)
      setLines(doc.lines.map(l => {
        const line: Line = {}
        for (const c of spec.lineColumns) line[c.key] = l[c.key] == null ? '' : String(l[c.key])
        return line
      }))
      setOpen(doc.header)
    } catch (e) {
      setErrors([e instanceof ApiError ? e.message : 'The document could not be opened.'])
    } finally { setBusy(false) }
  }

  function validate(): string[] {
    const found: string[] = []
    for (const f of spec.headerFields) {
      if (f.required && !header[f.key]?.trim()) found.push(`${f.label} is required.`)
    }
    const real = lines.filter(l => spec.lineColumns.some(c => l[c.key]?.trim()))
    if (real.length === 0) found.push('A document must have at least one line.')
    real.forEach((l, i) => {
      for (const c of spec.lineColumns) {
        if (c.required && !l[c.key]?.trim()) found.push(`Line ${i + 1}: ${c.label} is required.`)
      }
      if (Number(l[qtyKey] || 0) <= 0) {
        found.push(`Line ${i + 1}: quantity must be greater than zero.`)
      }
    })
    return found
  }

  async function save() {
    const found = validate()
    setErrors(found)
    if (found.length) return

    const payload = {
      header,
      lines: lines.filter(l => spec.lineColumns.some(c => l[c.key]?.trim())),
      ...(open ? { version: open.version } : {}),
    }
    setBusy(true)
    try {
      if (open) await api.update(`/${spec.path}/${String(open[spec.idKey])}`, payload)
      else await api.create(`/${spec.path}`, payload)
      setOpen(undefined); await load()
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'The document could not be saved.'])
    } finally { setBusy(false) }
  }

  async function moveTo(to: DocStatus) {
    let remarks: string | null = ''
    if (to === 'Cancelled') {
      remarks = window.prompt('Why is this document being cancelled?')
      if (remarks === null) return              // they changed their mind
      if (!remarks.trim()) { setErrors(['A reason is required to cancel.']); return }
    }
    setBusy(true)
    try {
      await api.patch(`/${spec.path}/${String(open![spec.idKey])}`,
                      { status: to, version: open!.version, remarks })
      setOpen(undefined); await load()
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'The status could not be changed.'])
    } finally { setBusy(false) }
  }

  // ------------------------------------------------------------------ list
  if (open === undefined) {
    const rows = list?.rows ?? []
    return (
      <>
        <h1 className="page-title">{spec.title}</h1>
        <p className="page-subtitle">{loading ? 'Loading…' : `${rows.length} documents`}</p>

        {errors.length > 0 && <div className="error-state">{errors[0]}</div>}

        <div className="card">
          <div className="toolbar">
            <div className="grow" />
            {rights.can('print') && (
            <button className="btn btn-secondary" disabled={!rows.length}
                    onClick={() => { void (async () => {
                      try {
                        saveFile(await api.get<ExportedFile>(`/${spec.path}/export`))
                      } catch (e) {
                        setErrors([e instanceof ApiError ? e.message
                          : 'The export could not be produced.'])
                      }
                    })() }}>
              Export to Excel
            </button>
            )}
            {rights.can('add') && (
              <button className="btn btn-primary" onClick={startNew}>
                New {singularOf(spec)}
              </button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr>
                {spec.listColumns.map(c => (
                  <th key={c.key} className={c.numeric ? 'num' : undefined}>{c.label}</th>
                ))}
                <th>Status</th>
              </tr></thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={spec.listColumns.length + 1} className="empty">
                    No {spec.title.toLowerCase()} yet.
                  </td></tr>
                )}
                {rows.map(r => (
                  <tr key={String(r[spec.idKey])} style={{ cursor: 'pointer' }}
                      onClick={() => void openDocument(r)}>
                    {spec.listColumns.map(c => (
                      <td key={c.key} className={c.numeric ? 'num' : undefined}>
                        {c.value && !valuesVisible
                          ? <span className="masked">••••</span>
                          : c.numeric ? money(r[c.key])
                          : String(r[c.key] ?? '—')}
                      </td>
                    ))}
                    <td>
                      <span className={`badge ${BADGE[r.status as DocStatus] ?? ''}`}>
                        {String(r.status ?? '—')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  }

  // -------------------------------------------------------------- document
  const status = (open?.status as DocStatus) ?? 'Draft'
  // A new document needs `add`; changing one needs `modify`. Without the
  // right it reads as a document, not a form that will refuse.
  const editable = !open ? rights.can('add')
    : (status === 'Draft' || status === 'Submitted') && rights.can('modify')
  // Authorising needs `approve`; every other move needs `modify`.
  const moves = NEXT[status].filter(
    to => rights.can(to === 'Authorised' ? 'approve' : 'modify'))

  return (
    <>
      <button className="link-btn" onClick={() => setOpen(undefined)}>
        ← {spec.title}
      </button>

      <h1 className="page-title" style={{ marginTop: 8 }}>
        {open ? String(open.doc_no ?? 'Draft') : `New ${singularOf(spec)}`}
        {open && (
          <span className={`badge ${BADGE[status]}`} style={{ marginLeft: 12, fontSize: 12 }}>
            {status}
          </span>
        )}
      </h1>
      <p className="page-subtitle">
        {open ? 'Opened from the register' : 'Not yet numbered — a draft burns no document number'}
      </p>

      {errors.length > 0 && (
        <div className="card form-card">
          <div className="form-errors" role="alert">
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        </div>
      )}

      {/* --- header ----------------------------------------------------- */}
      <div className="card form-card">
        <div className="card-header">Details</div>
        <div className="form-grid">
          {spec.headerFields.map(f => (
            <div key={f.key} className="form-field">
              <label htmlFor={`h-${f.key}`}>
                {f.label}{f.required && <span className="req"> *</span>}
              </label>
              <input
                id={`h-${f.key}`}
                type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                value={header[f.key] ?? ''}
                disabled={!editable}
                onChange={e => setHeader(h => ({ ...h, [f.key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      </div>

      {/* --- line grid --------------------------------------------------- */}
      <div className="card">
        <div className="card-header">Items</div>
        <div style={{ overflowX: 'auto' }}>
          <table className="grid">
            <thead><tr>
              <th style={{ width: 44 }}>#</th>
              {spec.lineColumns.map(c => (
                <th key={c.key} className={c.numeric ? 'num' : undefined}
                    style={c.width ? { width: c.width } : undefined}>
                  {c.label}{c.required && <span className="req"> *</span>}
                </th>
              ))}
              <th className="num" style={{ width: 130 }}>Amount</th>
              {editable && <th style={{ width: 44 }} />}
            </tr></thead>

            <tbody ref={gridRef}>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td className="num" style={{ color: 'var(--text-secondary)' }}>{i + 1}</td>
                  {spec.lineColumns.map(c => (
                    <GridCell
                      key={c.key} column={c} value={line[c.key] ?? ''}
                      editable={editable && !(c.value && !valuesVisible)}
                      masked={!!c.value && !valuesVisible}
                      onChange={v => setCell(i, c.key, v)}
                      onKeyDown={e => onGridKeyDown(e, i)}
                    />
                  ))}
                  <td className="num">
                    {!valuesVisible ? <span className="masked">••••</span>
                                    : money(lineAmount(line))}
                  </td>
                  {editable && (
                    <td>
                      <button className="link-btn" title="Remove this line"
                              onClick={() => removeLine(i)}>×</button>
                    </td>
                  )}
                </tr>
              ))}

              <tr className="total-row">
                <td colSpan={spec.lineColumns.length + 1} className="num">
                  Document total
                </td>
                <td className="num">
                  {!valuesVisible ? <span className="masked">••••</span> : money(total)}
                </td>
                {editable && <td />}
              </tr>
            </tbody>
          </table>
        </div>

        {editable && (
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={addLine}>+ Add line</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => setOpen(undefined)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* --- lifecycle ---------------------------------------------------- */}
      {open && moves.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">Actions</div>
          <div className="form-actions">
            {moves.map(to => (
              <button
                key={to}
                className={to === 'Cancelled' ? 'btn btn-secondary' : 'btn btn-primary'}
                disabled={busy}
                onClick={() => void moveTo(to)}
              >
                {ACTION_LABEL[to] ?? to}
              </button>
            ))}
          </div>
          {status === 'Authorised' && (
            <p className="form-note">
              An authorised document cannot be edited. To change it, cancel it
              and raise a replacement — so the change is visible rather than
              silently rewriting an approved figure.
            </p>
          )}
        </div>
      )}
    </>
  )
}

function GridCell(
  { column, value, editable, masked, onChange, onKeyDown }: {
    column: LineColumn
    value: string
    editable: boolean
    masked: boolean
    onChange: (v: string) => void
    onKeyDown: (e: React.KeyboardEvent) => void
  },
) {
  if (masked) return <td className="num masked">••••</td>
  if (!editable || !column.edit) {
    return <td className={column.numeric ? 'num' : undefined}>{value || '—'}</td>
  }
  return (
    <td className={column.numeric ? 'num' : undefined}>
      <input
        type={column.edit === 'number' ? 'number' : 'text'}
        value={value}
        list={column.lookup ? `lookup-${column.lookup}` : undefined}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className={column.numeric ? 'cell num' : 'cell'}
        aria-label={column.label}
      />
    </td>
  )
}
